import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check } from 'lucide-react';
import { GlassPage } from '../components/chrome';
import { GlassesModel } from '../components/model';
import { FeatureGrid, SectionHeading } from '../components/ui';
import { ALL_JOBS, FEATURES, FEATURES_NOTE, MARKETPLACE, PRODUCT_HALVES } from '../lib/content';

// --- Components ---

const FadeInSection = ({ children }) => {
  const [isVisible, setVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const node = domRef.current;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(node);
        }
      });
    }, { threshold: 0.1 });

    if (node) observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={domRef}
      className={`transition-all duration-1000 ease-out transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}
    >
      {children}
    </div>
  );
};

const ScrollRevealText = ({ content, className }) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const elementRef = useRef(null);

  useEffect(() => {
    let frame = 0;
    const handleScroll = () => {
      // Coalesce to one measurement per frame; this fires on every scroll tick
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (!elementRef.current) return;
        const { top, height } = elementRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        const startReveal = windowHeight * 0.8;
        const endReveal = windowHeight * 0.6;

        const distance = startReveal - top;
        const totalRange = startReveal - endReveal + height * 0.5;

        setScrollProgress(Math.max(0, Math.min(1, distance / totalRange)));
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const paragraphs = content.split('\n').filter(p => p.trim() !== '');
  const totalWordCount = paragraphs.join(' ').split(' ').filter(Boolean).length;
  const activeWordIndex = Math.ceil(scrollProgress * totalWordCount) - 1;

  let globalWordIndex = 0;

  return (
    <div ref={elementRef} className={className}>
      {paragraphs.map((paragraph, pIdx) => (
        <p key={pIdx} className="mb-6 md:mb-8 last:mb-0">
          {paragraph.split(' ').map((word, wIdx) => {
            const isWordActive = globalWordIndex <= activeWordIndex;
            globalWordIndex++;
            return (
              <span
                key={wIdx}
                className={`transition-colors duration-300 ${isWordActive ? 'text-zinc-900' : 'text-zinc-500'}`}
              >
                {word}{' '}
              </span>
            );
          })}
        </p>
      ))}
    </div>
  );
};

// External coverage about Korsa. Swap in real article links as they land.
const NEWS_ARTICLES = [
  {
    source: 'YouTube',
    date: 'Nov 2025',
    title: 'Korsa demos its 2025 prototype',
    blurb: 'The first working prototype on camera, with live word retrieval and teleprompting running on real hardware.',
    url: 'https://www.youtube.com/@Korsahq'
  },
  {
    source: 'X / Twitter',
    date: '2026',
    title: 'Follow the build in public',
    blurb: 'Product updates, milestones, and announcements straight from the team.',
    url: 'https://x.com/korsahq'
  },
  {
    source: 'TikTok',
    date: '2026',
    title: 'Korsa behind the scenes',
    blurb: 'Short-form looks at the glasses, the lab, and the people building them.',
    url: 'https://www.tiktok.com/@korsahq'
  }
];

const App = () => {
  // If we arrive with a hash (e.g. the order page's jump links point at
  // /#korsaos), scroll to it once the layout settles, after the global
  // ScrollToTop has reset to the top.
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.querySelector(hash);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
      else el.scrollIntoView({ behavior: 'smooth' });
    }, 250);
    return () => clearTimeout(t);
  }, []);

  return (
    <GlassPage>
      {/* Hero */}
      <section className="relative h-screen [height:100svh] overflow-hidden">
        <div
          className="absolute inset-0 z-1 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at center, rgba(255, 255, 255, 0) 30%, rgba(238, 241, 246, 0.85) 100%)',
          }}
        />

        <GlassesModel modelPath="/models/model.glb" className="absolute top-[30%] left-[2%] w-[96%] h-[52%] md:top-[16%] md:left-0 md:w-full md:h-full z-0 pointer-events-none" />

        {/* Headline at the top, clear of the glasses */}
        <div className="relative z-10 flex flex-col items-center text-center px-5 md:px-12 pt-24 md:pt-32">
          <h1 className="text-[2.6rem] leading-[0.95] sm:text-[7vw] md:text-[4.5vw] font-bold tracking-tighter mb-5 uppercase text-zinc-900">
            Think Clearly.<br/>
            <span className="text-zinc-500">Speak Confidently</span>
          </h1>

          <Link to="/order" className="btn-dark w-44">
            Pre-order
          </Link>
        </div>
      </section>

      {/* Mission */}
      <section id="mission" className="section">
        <FadeInSection>
          <div className="panel max-w-[1600px] mx-auto">
            <h2 className="text-[2.5rem] sm:text-6xl md:text-[7vw] font-bold tracking-tighter leading-[0.9] uppercase mb-10 md:mb-16 text-zinc-900 text-center">
              Our Mission.
            </h2>
            <ScrollRevealText
              className="max-w-3xl mx-auto text-lg sm:text-xl md:text-2xl font-medium leading-snug text-center"
              content={`Korsa is building the next generation of smart glasses using cognitive assistive technology to enhance how people communicate in everyday life.\nWe created smart glasses that support you as you talk. They look like ordinary glasses but to you, they are an essential piece of your day that makes communication easier.`}
            />
          </div>
        </FadeInSection>
      </section>

      {/* What Korsa is: the hardware and the software that drives it */}
      <section id="products" className="section scroll-mt-20">
        <FadeInSection>
          <div className="panel max-w-[1600px] mx-auto">
            <div className="grid md:grid-cols-2 gap-10 md:gap-16">
              {PRODUCT_HALVES.map((p) => (
                <div key={p.title} className="md:first:border-r md:first:border-zinc-900/5 md:first:pr-16">
                  <SectionHeading eyebrow={p.eyebrow} title={p.title} />
                  <ul className="mt-6 md:mt-8 space-y-3">
                    {p.points.map((point) => (
                      <li key={point} className="flex items-start gap-3">
                        <Check size={16} strokeWidth={2.6} className="mt-1 shrink-0 text-zinc-900" />
                        <span className="text-[15px] md:text-base text-zinc-700 leading-relaxed">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </FadeInSection>
      </section>

      {/* KorsaOS + Marketplace */}
      <section id="korsaos" className="section scroll-mt-20">
        <FadeInSection>
          <div className="panel max-w-[1600px] mx-auto">
            <h2 className="text-4xl md:text-6xl font-bold tracking-tighter text-zinc-900 text-center mb-8 md:mb-14">
              Korsa<span className="text-zinc-500">OS</span>
            </h2>

            <FeatureGrid items={FEATURES} centered />
            <p className="mt-6 text-sm text-zinc-600 text-center">{FEATURES_NOTE}</p>

            <div className="mt-10 md:mt-16 pt-10 md:pt-12 border-t border-zinc-900/5">
              <SectionHeading
                align="center"
                title="The marketplace"
                lead="One pair of glasses that keeps getting better. New abilities arrive the way apps do on a phone."
                className="mb-8 md:mb-10"
              />
              <FeatureGrid items={MARKETPLACE} columns={3} centered />
            </div>
          </div>
        </FadeInSection>
      </section>

      {/* Newsroom */}
      <section id="news" className="section">
        <FadeInSection>
          <div className="panel max-w-[1600px] mx-auto">
            <SectionHeading
              align="center"
              title="Newsroom"
              lead="Korsa in the news: press, coverage, and mentions."
              className="mb-10 md:mb-16"
            />

            <div className="grid gap-3 md:grid-cols-3 md:gap-6">
              {NEWS_ARTICLES.map((article) => (
                <a
                  key={article.title}
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col rounded-2xl bg-white/40 border border-white/70 p-5 md:p-8 hover-lift hover:bg-white/70 transition-colors duration-300"
                >
                  <div className="flex items-center justify-between mb-4 md:mb-6">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-900">{article.source}</span>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{article.date}</span>
                  </div>
                  <h3 className="text-lg md:text-2xl font-bold tracking-tight text-zinc-900 mb-2 md:mb-3">{article.title}</h3>
                  <p className="text-sm text-zinc-600 leading-relaxed flex-1">{article.blurb}</p>
                  <span className="mt-5 md:mt-6 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-900">
                    Visit {article.source}<span className="sr-only"> (opens in a new tab)</span>
                    <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </a>
              ))}
            </div>

            <div className="mt-10 md:mt-12 flex justify-center">
              <Link
                to="/contact"
                state={{ subject: 'Press Inquiry' }}
                className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 hover:text-zinc-900 transition-colors"
              >
                Press inquiries → Contact us
              </Link>
            </div>
          </div>
        </FadeInSection>
      </section>

      {/* Careers preview */}
      <section className="section">
        <FadeInSection>
          <div className="panel max-w-[1600px] mx-auto">
            <h2 className="text-[2.5rem] sm:text-6xl md:text-7xl font-bold tracking-tighter uppercase leading-[0.9] text-zinc-900 text-center mb-8 md:mb-16">
              Join our team
            </h2>
            <div className="border-t border-zinc-900/5">
              {ALL_JOBS.slice(0, 5).map((job) => (
                <Link
                  key={job.title}
                  to="/careers"
                  className="group border-b border-zinc-900/5 py-4 md:py-6 flex items-center justify-between gap-4 hover:bg-white/40 rounded-xl transition-colors duration-300 px-2 md:px-4"
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-0.5 md:gap-8">
                    <h3 className="text-[17px] md:text-xl font-bold tracking-tight text-zinc-900">{job.title}</h3>
                    <span className="text-xs text-zinc-600">{job.desc}</span>
                  </div>
                  <ArrowRight size={16} className="text-zinc-500 group-hover:text-zinc-900 group-hover:translate-x-1 transition-all shrink-0" />
                </Link>
              ))}
            </div>
            <div className="mt-10 md:mt-12 flex justify-center">
              <Link to="/careers" className="btn-dark">
                View all positions <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </FadeInSection>
      </section>
    </GlassPage>
  );
};

export default App;
